/**
 * Utility module for generating raw ESC/POS commands and printing to USB Thermal Receipt Printers (80mm / 48 columns).
 */

export function generateESCPOSReceipt(order, options = {}) {
  const encoder = new TextEncoder();
  const bytes = [];

  const addBytes = (arr) => bytes.push(...arr);
  const addText = (text) => bytes.push(...encoder.encode(text));

  const addRow = (leftText, rightText, width = 48) => {
    leftText = String(leftText || '');
    rightText = String(rightText || '');
    const spaceCount = width - leftText.length - rightText.length;
    if (spaceCount > 0) {
      addText(leftText + ' '.repeat(spaceCount) + rightText + '\n');
    } else {
      addText(leftText + '\n');
      addText(' '.repeat(Math.max(0, width - rightText.length)) + rightText + '\n');
    }
  };

  const lineSeparator = '-'.repeat(48) + '\n';

  // 1. Reset / Initialize Printer
  addBytes([0x1b, 0x40]);

  // 2. Header: RECEIPT (Centered, Bold, Double Size)
  addBytes([0x1b, 0x61, 0x01]); // Align Center
  addBytes([0x1b, 0x45, 0x01]); // Bold ON
  addBytes([0x1d, 0x21, 0x11]); // Double height & width
  addText('RECEIPT\n');
  addBytes([0x1d, 0x21, 0x00]); // Reset text size

  // 3. Date & Org Name (Centered)
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(now.getDate());
  const month = pad(now.getMonth() + 1);
  const year = String(now.getFullYear()).slice(-2);
  const hours = pad(now.getHours());
  const minutes = pad(now.getMinutes());
  const dateStr = `${day}/${month}/${year} ${hours}:${minutes}`;

  addText(dateStr + '\n');

  addBytes([0x1b, 0x45, 0x01]); // Bold ON
  addText((options.orgName || 'Ketco') + '\n\n');
  addBytes([0x1b, 0x45, 0x00]); // Bold OFF

  // 4. Separator Line
  addBytes([0x1b, 0x61, 0x00]); // Align Left
  addText(lineSeparator);

  // 5. Items List
  const items = order.orderItems || order.cartItemList || order.items || [];
  items.forEach((item) => {
    let name = item.item1Name || item.name || item.title || 'Item';
    if (item.name && item.item1Name && item.name !== item.item1Name) {
      name = `${item.item1Name} ${item.name}`;
    }
    const qty = item.qty || item.quantity || 1;
    const subtotal = Number(item.total).toFixed(2);
    const unitPrice = Number((subtotal / qty)).toFixed(2);

    // Item Name (Bold)
    addBytes([0x1b, 0x45, 0x01]);
    addText(name + '\n');
    addBytes([0x1b, 0x45, 0x00]);

    // Qty x Price and Subtotal
    const leftDetail = `  ${unitPrice} x ${qty}`;
    addRow(leftDetail, subtotal);
  });

  // 6. Separator Line & Total
  addText(lineSeparator);
  addBytes([0x1b, 0x45, 0x01]); // Bold ON
  const totalVal = Number(order.total || 0).toFixed(2);
  addRow('TOTAL:', `INR ${totalVal}`);
  addBytes([0x1b, 0x45, 0x00]); // Bold OFF
  addText(lineSeparator);

  // 6.5. Note Section (if present)
  const noteVal = String(order.note || order.paymentNote || order.notes || '').trim();
  if (noteVal !== '') {
    addBytes([0x1b, 0x61, 0x00]); // Align Left
    addText(`Note: ${noteVal}\n`);
    addText(lineSeparator);
  }

  // 7. Footer (Centered)
  addBytes([0x1b, 0x61, 0x01]); // Align Center
  addText('Thank you for your business.\n');
  addBytes([0x1b, 0x45, 0x01]); // Bold ON
  const orderNbr = order.orderNbrShort || order.orderShortNbr || order.orderNbr || '';
  if (orderNbr) {
    addText(orderNbr + '\n');
  }
  addBytes([0x1b, 0x45, 0x00]); // Bold OFF

  // 8. Feed paper & Cut
  addBytes([0x1b, 0x64, 0x05]); // ESC d 5: Feed 5 lines past print head to cutter blade
  addBytes([0x1d, 0x56, 0x41, 0x00]); // GS V 65 0: Full Cut after feed

  return new Uint8Array(bytes);
}

/**
 * Finds the best matching USB printer device from already paired browser USB devices.
 */
function selectUSBPrinterDevice(devices) {
  if (!devices || devices.length === 0) return null;

  // 1. Check if stored printer in localStorage matches any device
  try {
    const storedStr = localStorage.getItem('lastUsedPrinter');
    if (storedStr) {
      const stored = JSON.parse(storedStr);
      const matched = devices.find(d =>
        (stored.serialNumber && d.serialNumber === stored.serialNumber) ||
        (stored.vendorId && d.vendorId === stored.vendorId && stored.productId === d.productId)
      );
      if (matched) return matched;
    }
  } catch (e) {
    // ignore
  }

  // 2. Pass 1: Check for device with Vendor-Specific Class (255 / 0xff)
  for (const dev of devices) {
    if (dev.configurations) {
      for (const config of dev.configurations) {
        for (const iface of config.interfaces) {
          for (const alt of iface.alternates) {
            if (alt.interfaceClass === 255) return dev;
          }
        }
      }
    }
  }

  // 3. Pass 2: Check for device with bulk OUT endpoint
  for (const dev of devices) {
    if (dev.configurations) {
      for (const config of dev.configurations) {
        for (const iface of config.interfaces) {
          for (const alt of iface.alternates) {
            if (alt.endpoints && alt.endpoints.some(e => e.direction === 'out' && e.type === 'bulk')) {
              return dev;
            }
          }
        }
      }
    }
  }

  // 4. Fallback to first USB device
  return devices[0];
}

/**
 * Save printer details to localStorage for future auto-connection.
 */
function savePrinterToStorage(device) {
  if (!device) return;
  try {
    localStorage.setItem('lastUsedPrinter', JSON.stringify({
      vendorId: device.vendorId,
      productId: device.productId,
      serialNumber: device.serialNumber
    }));
    localStorage.removeItem('lastUsedPrinterType');
  } catch (e) {
    console.warn('Failed to save printer to localStorage:', e);
  }
}

/**
 * Transmits binary ESC/POS payload to a WebUSB device without prompt options.
 */
async function sendToWebUSBDevice(device, bytes) {
  if (!device) throw new Error('No WebUSB printer device provided.');

  try {
    if (!device.opened) {
      await device.open();
    }

    // 1. Ensure configuration is selected if missing
    if (device.configuration === null) {
      const configValue = (device.configurations && device.configurations.length > 0)
        ? device.configurations[0].configurationValue
        : 1;
      try {
        await device.selectConfiguration(configValue);
      } catch (e) {
        console.warn('selectConfiguration warning:', e);
      }
    }

    // 2. Discover all candidate OUT endpoints
    const candidates = [];
    if (device.configuration && device.configuration.interfaces) {
      for (const iface of device.configuration.interfaces) {
        for (const alt of iface.alternates) {
          for (const ep of (alt.endpoints || [])) {
            if (ep.direction === 'out') {
              candidates.push({
                interfaceNumber: iface.interfaceNumber,
                alternateSetting: alt.alternateSetting,
                endpointNumber: ep.endpointNumber,
                isVendorClass: alt.interfaceClass === 255,
                isPrinterClass: alt.interfaceClass === 7,
                isBulk: ep.type === 'bulk'
              });
            }
          }
        }
      }
    }

    // Sort candidates:
    // Class 7 (Printer Class) is blocked by Chrome WebUSB as a "protected class".
    // Therefore, non-protected & Vendor-Specific (255) interfaces MUST be tried first.
    candidates.sort((a, b) => {
      if (a.isPrinterClass !== b.isPrinterClass) {
        return a.isPrinterClass ? 1 : -1; // Put Class 7 last
      }
      if (a.isVendorClass !== b.isVendorClass) {
        return a.isVendorClass ? -1 : 1; // Put Vendor Class 255 first
      }
      if (a.isBulk !== b.isBulk) {
        return a.isBulk ? -1 : 1;
      }
      return 0;
    });

    if (candidates.length === 0) {
      candidates.push({
        interfaceNumber: 0,
        alternateSetting: 0,
        endpointNumber: 1,
        isVendorClass: false,
        isPrinterClass: false,
        isBulk: true
      });
    }

    // 3. Claim interface and select alternate interface
    let selectedCandidate = null;
    let lastError = null;

    for (const cand of candidates) {
      try {
        await device.claimInterface(cand.interfaceNumber);
        if (typeof device.selectAlternateInterface === 'function') {
          try {
            await device.selectAlternateInterface(cand.interfaceNumber, cand.alternateSetting);
          } catch (altErr) {
            console.warn(`selectAlternateInterface(${cand.interfaceNumber}, ${cand.alternateSetting}) warning:`, altErr);
          }
        }
        selectedCandidate = cand;
        break;
      } catch (e) {
        console.warn(`Failed to claim USB interface ${cand.interfaceNumber}:`, e);
        lastError = e;
      }
    }

    if (!selectedCandidate) {
      let errorMsg = 'Could not claim USB printer interface.';
      if (lastError) {
        const errStr = String(lastError.message || lastError);
        if (lastError.name === 'SecurityError' || errStr.toLowerCase().includes('protected class') || errStr.toLowerCase().includes('access denied')) {
          errorMsg = 'This USB printer uses standard Printer Class (0x07) which Chrome blocks via WebUSB.';
        } else {
          errorMsg += ` ${errStr}`;
        }
      } else {
        errorMsg += ' The device interface may be in use by another application or OS print driver.';
      }
      throw new Error(errorMsg);
    }

    // 4. Perform transferOut and ensure resources are cleaned up
    try {
      await device.transferOut(selectedCandidate.endpointNumber, bytes);
    } finally {
      try {
        await device.releaseInterface(selectedCandidate.interfaceNumber);
      } catch (e) {
        // ignore
      }
      try {
        await device.close();
      } catch (e) {
        // ignore
      }
    }

    return true;
  } catch (outerErr) {
    const errStr = String(outerErr.message || outerErr);
    if (outerErr.name === 'SecurityError' || errStr.toLowerCase().includes('protected class') || errStr.toLowerCase().includes('access denied')) {
      throw new Error('This USB printer uses standard Printer Class (0x07) which Chrome blocks via WebUSB.');
    }
    throw outerErr;
  }
}

/**
 * Print via WebUSB API.
 */
export async function printViaWebUSB(bytes) {
  if (!('usb' in navigator)) {
    throw new Error('WebUSB API is not supported in this browser.');
  }
  const devices = await navigator.usb.getDevices().catch(() => []);
  let device = selectUSBPrinterDevice(devices);

  if (!device) {
    device = await navigator.usb.requestDevice({ filters: [] });
  }

  if (!device) {
    throw new Error('No USB printer connected.');
  }

  savePrinterToStorage(device);
  return await sendToWebUSBDevice(device, bytes);
}

export function printViaSystem() {
  window.print();
  return true;
}

export function promptUSBPrinterSelection(order, options = {}) {
  return printESCPOSDirect(order, options);
}

export class WebUSBReceiptPrinter {
  constructor() {
    this.device = null;
    this.listeners = {};
  }

  addEventListener(event, callback) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  emit(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach(cb => cb(data));
    }
  }

  async connect() {
    if (!('usb' in navigator)) {
      throw new Error('WebUSB API is not supported in this browser.');
    }
    const devices = await navigator.usb.getDevices().catch(() => []);
    let device = selectUSBPrinterDevice(devices);

    if (!device) {
      device = await navigator.usb.requestDevice({ filters: [] });
    }

    if (!device) {
      throw new Error('No USB printer device selected.');
    }
    this.device = device;
    this.emit('connected', device);
    return device;
  }

  async print(bytes) {
    if (!this.device) {
      throw new Error('No printer device connected.');
    }
    return await sendToWebUSBDevice(this.device, bytes);
  }
}

/**
 * ESC/POS receipt printing that sends binary ESC/POS commands directly to the connected USB printer.
 */
export async function printESCPOSDirect(order, options = {}) {
  const bytes = generateESCPOSReceipt(order, options);
  console.log("printESCPOSDirect 1 - ESC/POS payload generated.");

  if (!('usb' in navigator)) {
    throw new Error('WebUSB API is not supported in this browser.');
  }

  // 1. Check already paired WebUSB devices
  let pairedUSBDevice = null;
  try {
    const devices = await navigator.usb.getDevices().catch(() => []);
    if (devices && devices.length > 0) {
      pairedUSBDevice = selectUSBPrinterDevice(devices);
    }
  } catch (e) {
    console.warn('Error checking WebUSB devices:', e);
  }

  // If paired device exists, try printing directly
  if (pairedUSBDevice) {
    try {
      console.log("printESCPOSDirect - Printing raw ESC/POS directly to paired WebUSB printer:", pairedUSBDevice.productName || pairedUSBDevice.vendorId);
      await sendToWebUSBDevice(pairedUSBDevice, bytes);
      return true;
    } catch (err) {
      console.warn("Failed printing to paired USB device:", err);
    }
  }

  // 2. Request user to select a WebUSB printer
  try {
    console.log("printESCPOSDirect - Requesting WebUSB printer connection for ESC/POS printing.");
    const device = await navigator.usb.requestDevice({ filters: [] });
    if (device) {
      await sendToWebUSBDevice(device, bytes);
      savePrinterToStorage(device);
      return true;
    }
  } catch (err) {
    if (err.name === 'NotFoundError') {
      throw new Error('USB printer selection was cancelled.');
    }
    throw err;
  }

  throw new Error('Could not connect to USB thermal printer for ESC/POS printing.');
}

